import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Loader, Alert, ScrollArea } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import type { AmendmentCommentContext } from "../../hooks/useAmendmentDiagnosticDraft";
import { useDocumentViewData } from "../../hooks/useDocumentView";
import { ReportDocumentSection as DocumentSection } from "./ReportDocumentSection";
import { DocumentFooter } from "./DocumentHeader";
import { DiagnosisMacroItem } from "./RichTextDiagnosis";
import {
  type DocumentViewResponse,
  TemplateSection,
} from "../../types/reportTemplate";
import type { FragmentCountQualifier } from "../../utils/fragmentCount";
import { CLINICAL_RICH_TEXT_STYLES } from "../../utils/clinicalRichText";
import type { OrganMapPhoto } from "./ReportPhotoContent";
import {
  hasApplicableOrganMap,
  isOrganMapSection,
} from "./reportOrganMapLayout";
import {
  calculateRenderedPageOverflow,
  createReportPaginationLayout,
  findNextReportNarrativeKeyToFragment,
} from "./reportPagination";
import {
  REPORT_PAGE_FLOW_STYLE,
  REPORT_PAGE_LAYOUT,
  REPORT_PRINT_PAGE_STYLES,
} from "../../utils/reportPageLayout";
import {
  insertReportSupplementNotices,
  selectSupplementNoticeFragments,
} from "./reportSupplementNoticeSections";
import { groupReportSupplements } from "./reportSupplementGroups";
import {
  buildAddendumReportFragments,
  buildRichTextReportFragments,
} from "./addendumReportFragments";
import { buildReportSupplementNoticeFragments } from "./reportSupplementNoticeFragments";
import { isEmptyHtml } from "../../utils/helpers";
import {
  buildReportNarrativeSections,
  buildSpecimenDescriptionReportSections,
  isSpecimenDescriptionSection,
} from "./specimenDescriptionReportFragments";
import { resolvePatientLabelName } from "../../utils/patientLabelName";
import {
  buildLegacyHistoricalSections,
  historicalPatientName,
  historicalSupplementSections,
} from "./legacyHistoricalReportLayout";
import { hasUnavailableHistoricalAssets } from "../../utils/legacyHistoricalReportAssets";
import { LegacyHistoricalReportUnavailable } from "./LegacyHistoricalReportUnavailable";
import { LegacyHistoricalFooter } from "./LegacyHistoricalFooter";
import { observeReportLayout } from "./reportLayoutObservation";

// US Letter at 96 dpi (CSS pixels). Per GI report spec:
// top 36pt (48px), bottom 33pt (44px), left/right 36pt (48px).
const LETTER_PAGE_WIDTH = REPORT_PAGE_LAYOUT.widthPx;
const LETTER_PAGE_HEIGHT = REPORT_PAGE_LAYOUT.heightPx;
const LETTER_MARGIN_TOP = REPORT_PAGE_LAYOUT.marginTopPx;
const LETTER_MARGIN_BOTTOM = REPORT_PAGE_LAYOUT.marginBottomPx;
const LETTER_MARGIN_H = REPORT_PAGE_LAYOUT.marginHorizontalPx;
// Reserve for the DocumentFooter row rendered on every page. Its height isn't
// included in the measured section heights.
// The rendered row is ~30.4px; 34px preserves a small local cushion in
// addition to the page-level safety buffer below.
const LETTER_FOOTER_RESERVE = 34;
const HISTORICAL_FOOTER_EXTRA_RESERVE = 24;
// Small cushion for print-engine rendering variance (font metrics differ
// fractionally between screen and print). The measurement container mirrors
// the rendered .pdf-page typography exactly, so we don't need a large buffer.
const PAGE_CONTENT_SAFETY_BUFFER = 16;
const RENDERED_OVERFLOW_CUSHION = 8;
const MAX_OVERFLOW_REPAIR_PASSES = 3;
const PAGE_CONTENT_H =
  LETTER_PAGE_HEIGHT -
  LETTER_MARGIN_TOP -
  LETTER_MARGIN_BOTTOM -
  LETTER_FOOTER_RESERVE -
  PAGE_CONTENT_SAFETY_BUFFER;

const REPORT_MEDIA_SECTION: TemplateSection = {
  type: "diagnostic_map",
  label: "",
};

const STANDALONE_ADDENDUM_HEADER_SECTION: TemplateSection = {
  type: "header",
  label: "Header",
};

const STANDALONE_ADDENDUM_SECTION_TYPES = new Set<TemplateSection["type"]>([
  "header",
  "addendum",
  "disclaimers",
]);

const EMPTY_NARRATIVE_FRAGMENT_KEYS: ReadonlySet<string> = new Set();

function insertReportMediaSection(
  sections: TemplateSection[]
): TemplateSection[] {
  const lastDiagnosisIndex = sections.reduce(
    (foundIndex, section, index) =>
      section.type === "diagnosis" ? index : foundIndex,
    -1
  );
  const insertionIndex = lastDiagnosisIndex >= 0 ? lastDiagnosisIndex + 1 : 1;
  return [
    ...sections.slice(0, insertionIndex),
    REPORT_MEDIA_SECTION,
    ...sections.slice(insertionIndex),
  ];
}

function isStandaloneAddendumDocument(
  sections: TemplateSection[],
  reportAddendumCount: number,
  suppressSupplementNotices: boolean
): boolean {
  return (
    reportAddendumCount > 0 &&
    suppressSupplementNotices &&
    sections.every((section) =>
      STANDALONE_ADDENDUM_SECTION_TYPES.has(section.type)
    )
  );
}

function ensureStandaloneAddendumHeader(
  sections: TemplateSection[],
  standaloneAddendumDocument: boolean
): TemplateSection[] {
  if (
    !standaloneAddendumDocument ||
    sections.some((section) => section.type === "header")
  ) {
    return sections;
  }
  return [STANDALONE_ADDENDUM_HEADER_SECTION, ...sections];
}

interface GrossCode {
  value: string;
  label: string;
  description?: string;
  scenarioDescription?: string;
  fragmentCount?: number | null;
  fragmentCountQualifier?: FragmentCountQualifier | null;
}

const DOCUMENT_STYLES = `
  ${CLINICAL_RICH_TEXT_STYLES}

  .editable-section-inline-start p:first-child,
  .editable-section-inline-start p:first-of-type {
    margin-top: 0 !important;
    padding-top: 0 !important;
    margin-block-start: 0 !important;
  }

  .report-comments .report-comment-body p:first-child,
  .report-comments .report-comment-body p:first-of-type {
    margin-top: 0 !important;
  }

  .report-comments .report-comment-body p {
    margin-bottom: 0;
    margin-top: 4px;
  }

  .document-view-container {
    box-shadow:
      0 1px 1px rgba(0,0,0,0.12),
      0 2px 2px rgba(0,0,0,0.12),
      0 4px 4px rgba(0,0,0,0.12),
      0 8px 8px rgba(0,0,0,0.12),
      0 16px 16px rgba(0,0,0,0.12);
    border: 1px solid rgba(0, 0, 0, 0.08);
  }

  @media print {
    .document-view-container {
      padding: 0 !important;
      max-width: 100% !important;
      box-shadow: none !important;
      border: none !important;
    }

    ${REPORT_PRINT_PAGE_STYLES}
  }
`;

interface SpecimenDiagnosisData {
  specimenId: string;
  macroCode?: string;
  diagnosisText: string;
  microscopicDescription: string;
  location?: string | null;
  category?: string | null;
  cptCode?: string | null;
  icdCode?: string | null;
  icdCodes?: string[];
  color?: string | null;
  sizeCm?: string | null;
}

interface SpecimenGrossingData {
  specimenId: string;
  grossCode?: string;
  grossCodeId?: string;
  grossDescription: string;
  fragmentCount?: number | null;
  fragmentCountQualifier?: FragmentCountQualifier | null;
  variableValues?: Record<string, string>;
}

interface Specimen {
  id: string;
  number: string;
  sourceName: string;
  sourceId?: string | null;
  subsiteName?: string | null;
  findings?: string | null;
  collectionMethod?: string | null;
  blockCount?: number | null;
  // Organ map fields (from source.organMapTemplate and subsite positions)
  organMapTemplateId?: string | null;
  organMapTemplateName?: string | null;
  organMapImageUrl?: string | null;
  organMapWidth?: number | null;
  organMapHeight?: number | null;
  subsitePositionX?: number | null;
  subsitePositionY?: number | null;
}

interface CaseData {
  id: string;
  caseNumber: string;
  clinicalHistoryNotes?: string;
  indications?: string;
  impressions?: string;
  isFinalized: boolean;
  signedOffAt?: string;
  dateReceived?: string;
  dateCollected?: string;
  procedure?: string;
  physicianId?: string;
  sendingPracticeId?: string;
  surgeryCenterLabId?: string;
  patient?: {
    firstName?: string;
    lastName?: string;
    dateOfBirth?: string;
    medicalRecordNumber?: string;
  };
  physician?: {
    firstName?: string;
    lastName?: string;
    credentials?: string;
  };
  signedOffBy?: {
    fullName?: string;
    credentials?: string;
  };
}

interface PathologistCommentItem {
  id: string;
  commentText: string;
  createdAt: string;
  createdBy: { fullName: string; credentials: string | null } | null;
}

interface DocumentViewProps {
  caseData: CaseData;
  specimens: Specimen[];
  diagnosisDataMap: Map<string, SpecimenDiagnosisData>;
  grossingDataMap: Map<string, SpecimenGrossingData>;
  clinicalHistoryNotes: string;
  indications: string;
  impressions: string;
  pathologistComment: string;
  pathologistComments?: PathologistCommentItem[];
  amendmentCommentContext?: AmendmentCommentContext;
  frozenInterpretation?: string;
  isReadOnly: boolean;
  templateId?: string;
  /** Pin code facts to the immutable billing snapshot for a signed report. */
  reportVersion?: number;
  documentViewDataOverride?: DocumentViewResponse;
  includeLegacyBaseline?: boolean;
  reviewSimulation?: boolean;
  /** Skip the outer ScrollArea so the parent can control scrolling (e.g. in preview modal) */
  noScrollWrapper?: boolean;
  /** Render as Letter-size page boxes instead of a continuous scroll */
  paginate?: boolean;
  /** Physical pages appended after the main report, included in footer totals. */
  additionalPageCount?: number;
  /** Physical pages rendered before this document in a composite report. */
  pageNumberOffset?: number;
  onClinicalHistoryChange?: (value: string) => void;
  onIndicationsChange?: (value: string) => void;
  onImpressionsChange?: (value: string) => void;
  onCommentChange?: (value: string) => void;
  onDiagnosisChange?: (
    specimenId: string,
    field:
      | "diagnosisText"
      | "microscopicDescription"
      | "color"
      | "sizeCm"
      | "location",
    value: string
  ) => void;
  onGrossChange?: (specimenId: string, value: string) => void;
  onAddAddendum?: () => void;
  onAddAmendment?: () => void;
  onAddCorrection?: () => void;
  onCodesChange?: (type: "cpt" | "icd", displayCodes: string[]) => void;
  liveCptDisplay?: string[] | null;
  liveIcdDisplay?: string[] | null;
  onHeaderSaved?: () => void;
  macros?: DiagnosisMacroItem[];
  onMacroApplied?: (specimenId: string, macro: DiagnosisMacroItem) => void;
  grossCodes?: GrossCode[];
  onGrossCodeApplied?: (
    specimenId: string,
    data: {
      grossCode: string;
      grossCodeId: string;
      grossDescription: string;
      fragmentCount: number | null;
      fragmentCountQualifier: FragmentCountQualifier | null;
    }
  ) => void;
  onGrossVariableChange?: (
    specimenId: string,
    varName: string,
    value: string
  ) => void;
  subsiteOptionsBySource?: Record<
    string,
    Array<{ value: string; label: string }>
  >;
  /** Diagnosis categories for organ map marker shape/color lookup */
  diagnosisCategories?: Array<{
    name: string;
    markerShape: string | null;
    markerColor: string | null;
  }>;
  /**
   * Called once after pagination has settled with correct font metrics.
   * Used by the headless capture path to know when page breaks are final.
   */
  onPaginationSettled?: (pageCount: number) => void;
  /** Called whenever inputs change and pagination must be measured again. */
  onPaginationPending?: () => void;
  /** Called when rendered content cannot be fitted without clipping. */
  onPaginationFailed?: () => void;
  /** Selected photos rendered alongside organ maps. */
  reportPhotos?: OrganMapPhoto[];
  addendumReportPhotos?: OrganMapPhoto[];
}

interface DescriptionSectionLayout {
  diagnosisContents: string[];
  microscopicContents: string[];
  grossContents: string[];
  combinedMicroscopicContent: string;
  combinedGrossContent: string;
}

interface DescriptionSectionLayoutInput {
  specimens: Specimen[];
  diagnosisDataMap: Map<string, SpecimenDiagnosisData>;
  grossingDataMap: Map<string, SpecimenGrossingData>;
}

interface NarrativeFragmentationState {
  contentKey: string;
  fragmentedKeys: ReadonlySet<string>;
}

function createDescriptionSectionLayout({
  specimens,
  diagnosisDataMap,
  grossingDataMap,
}: DescriptionSectionLayoutInput): DescriptionSectionLayout {
  const diagnosisContents = specimens.map(
    (specimen) => diagnosisDataMap.get(specimen.id)?.diagnosisText || ""
  );
  const microscopicContents = specimens.map(
    (specimen) =>
      diagnosisDataMap.get(specimen.id)?.microscopicDescription || ""
  );
  const grossContents = specimens.map(
    (specimen) => grossingDataMap.get(specimen.id)?.grossDescription || ""
  );
  return {
    diagnosisContents,
    microscopicContents,
    grossContents,
    combinedMicroscopicContent: Array.from(diagnosisDataMap.values())
      .map((diagnosis) => diagnosis.microscopicDescription)
      .filter((value) => !isEmptyHtml(value))
      .join("\n\n"),
    combinedGrossContent: Array.from(grossingDataMap.values())
      .map((grossing) => grossing.grossDescription)
      .filter((value) => !isEmptyHtml(value))
      .join("\n\n"),
  };
}

export function DocumentView({
  caseData,
  specimens,
  diagnosisDataMap,
  grossingDataMap,
  clinicalHistoryNotes,
  indications,
  impressions,
  pathologistComment,
  pathologistComments,
  amendmentCommentContext,
  frozenInterpretation,
  isReadOnly,
  templateId,
  reportVersion,
  documentViewDataOverride,
  includeLegacyBaseline = true,
  reviewSimulation = false,
  noScrollWrapper = false,
  paginate = false,
  additionalPageCount = 0,
  pageNumberOffset = 0,
  onClinicalHistoryChange,
  onIndicationsChange,
  onImpressionsChange,
  onCommentChange,
  onDiagnosisChange,
  onGrossChange,
  onAddAddendum,
  onAddAmendment,
  onAddCorrection,
  onCodesChange,
  liveCptDisplay,
  liveIcdDisplay,
  onHeaderSaved,
  macros,
  onMacroApplied,
  grossCodes,
  onGrossCodeApplied,
  onGrossVariableChange,
  subsiteOptionsBySource,
  diagnosisCategories,
  onPaginationSettled,
  onPaginationPending,
  onPaginationFailed,
  reportPhotos = [],
  addendumReportPhotos = [],
}: DocumentViewProps) {
  const documentViewQuery = useDocumentViewData(
    caseData.id,
    templateId,
    reportVersion,
    !documentViewDataOverride
  );
  const documentViewData = documentViewDataOverride || documentViewQuery.data;
  const isLoading = !documentViewDataOverride && documentViewQuery.isLoading;
  const error = !documentViewDataOverride ? documentViewQuery.error : null;
  const historicalAssetsUnavailable = useMemo(
    () =>
      Boolean(
        documentViewData?.legacyBaseline &&
          hasUnavailableHistoricalAssets(
            documentViewData.legacyBaseline,
            documentViewData.legacyBaselineAssets
          )
      ),
    [documentViewData]
  );
  const footerPatientName = useMemo(
    () =>
      documentViewData?.legacyBaseline
        ? historicalPatientName(
            documentViewData.legacyReportHeader ??
              documentViewData.legacyBaseline.header
          )
        : resolvePatientLabelName(caseData.patient) ?? "",
    [caseData.patient, documentViewData]
  );
  const footerCaseNumber = useMemo(
    () =>
      documentViewData?.legacyReportHeader?.caseNumber ??
      documentViewData?.legacyBaseline?.header.caseNumber ??
      caseData.caseNumber ??
      "",
    [caseData.caseNumber, documentViewData]
  );
  const pageContentHeight = useMemo(
    () =>
      PAGE_CONTENT_H -
      (documentViewData?.legacyBaseline ? HISTORICAL_FOOTER_EXTRA_RESERVE : 0),
    [documentViewData?.legacyBaseline]
  );
  const specimenSectionLayoutKey = specimens
    .map((specimen) =>
      hasApplicableOrganMap(specimen)
        ? `${specimen.id}:${specimen.organMapTemplateId}`
        : `${specimen.id}:no-map`
    )
    .join("|");
  const sectionLayoutSpecimensRef = useRef({
    key: specimenSectionLayoutKey,
    specimens,
  });
  if (sectionLayoutSpecimensRef.current.key !== specimenSectionLayoutKey) {
    sectionLayoutSpecimensRef.current = {
      key: specimenSectionLayoutKey,
      specimens,
    };
  }
  const sectionLayoutSpecimens = sectionLayoutSpecimensRef.current.specimens;
  const nextDescriptionSectionLayout = useMemo(
    () =>
      createDescriptionSectionLayout({
        specimens: sectionLayoutSpecimens,
        diagnosisDataMap,
        grossingDataMap,
      }),
    [sectionLayoutSpecimens, diagnosisDataMap, grossingDataMap]
  );
  const descriptionSectionLayoutKey = useMemo(
    () => JSON.stringify(nextDescriptionSectionLayout),
    [nextDescriptionSectionLayout]
  );
  const descriptionSectionLayoutRef = useRef({
    key: descriptionSectionLayoutKey,
    layout: nextDescriptionSectionLayout,
  });
  if (descriptionSectionLayoutRef.current.key !== descriptionSectionLayoutKey) {
    descriptionSectionLayoutRef.current = {
      key: descriptionSectionLayoutKey,
      layout: nextDescriptionSectionLayout,
    };
  }
  const descriptionSectionLayout = descriptionSectionLayoutRef.current.layout;
  const commentSectionLayoutKey = useMemo(
    () =>
      JSON.stringify(
        (pathologistComments || []).map(({ id, commentText }) => ({
          id,
          commentText,
        }))
      ),
    [pathologistComments]
  );
  const commentSectionLayoutRef = useRef({
    key: commentSectionLayoutKey,
    comments: pathologistComments || [],
  });
  if (commentSectionLayoutRef.current.key !== commentSectionLayoutKey) {
    commentSectionLayoutRef.current = {
      key: commentSectionLayoutKey,
      comments: pathologistComments || [],
    };
  }
  const commentSectionLayout = commentSectionLayoutRef.current.comments;
  const narrativeFragmentsByContent = useMemo(() => {
    const contents = [
      ...descriptionSectionLayout.diagnosisContents,
      ...descriptionSectionLayout.microscopicContents,
      ...descriptionSectionLayout.grossContents,
      descriptionSectionLayout.combinedMicroscopicContent,
      descriptionSectionLayout.combinedGrossContent,
      ...commentSectionLayout.map((comment) => comment.commentText),
    ];
    return new Map(
      Array.from(new Set(contents)).map((content) => [
        content,
        buildRichTextReportFragments(content, {
          splitAtEveryBreakpoint: true,
        }),
      ])
    );
  }, [commentSectionLayout, descriptionSectionLayout]);
  const paginationContentKey = useMemo(
    () =>
      JSON.stringify({
        caseData,
        specimens,
        diagnoses: Array.from(diagnosisDataMap.entries()),
        grossing: Array.from(grossingDataMap.entries()),
        clinicalHistoryNotes,
        indications,
        impressions,
        pathologistComment,
        pathologistComments,
        frozenInterpretation,
        liveCptDisplay,
        liveIcdDisplay,
        diagnosisCategories,
        reviewSimulation,
      }),
    [
      caseData,
      clinicalHistoryNotes,
      diagnosisCategories,
      diagnosisDataMap,
      frozenInterpretation,
      grossingDataMap,
      impressions,
      indications,
      liveCptDisplay,
      liveIcdDisplay,
      pathologistComment,
      pathologistComments,
      reviewSimulation,
      specimens,
    ]
  );
  const [narrativeFragmentation, setNarrativeFragmentation] =
    useState<NarrativeFragmentationState>({
      contentKey: "",
      fragmentedKeys: EMPTY_NARRATIVE_FRAGMENT_KEYS,
    });
  const fragmentedNarrativeKeys = useMemo(
    () =>
      narrativeFragmentation.contentKey === paginationContentKey
        ? narrativeFragmentation.fragmentedKeys
        : EMPTY_NARRATIVE_FRAGMENT_KEYS,
    [narrativeFragmentation, paginationContentKey]
  );

  // Unroll the template sections for pagination purposes.
  // Diagnosis sections are split into individual specimen entries so the
  // pagination logic can place breaks between specimens rather than treating
  // the entire multi-specimen diagnosis as one indivisible block.
  const sections = useMemo(() => {
    if (!documentViewData) return [];
    const supplementGroups = groupReportSupplements(
      documentViewData.addendums || []
    );
    const reportAddendums =
      supplementGroups.find((group) => group.type === "addendum")?.entries ??
      [];
    const sourceTemplateSections = documentViewData.legacyBaseline
      ? includeLegacyBaseline
        ? buildLegacyHistoricalSections(documentViewData.legacyBaseline)
        : historicalSupplementSections()
      : documentViewData.template.templateContent.sections;
    const templateSections = sourceTemplateSections.filter(
      (section) => section.type !== "addendum" || reportAddendums.length > 0
    );
    const suppressSupplementNotices =
      Boolean(documentViewData.legacyBaseline) ||
      documentViewData.template.templateContent.metadata
        ?.suppressSupplementNotices === true;
    const sectionsWithHeader = ensureStandaloneAddendumHeader(
      templateSections,
      isStandaloneAddendumDocument(
        templateSections,
        reportAddendums.length,
        suppressSupplementNotices
      )
    );
    const sectionsWithNotices =
      supplementGroups.length > 0 && !suppressSupplementNotices
        ? insertReportSupplementNotices(sectionsWithHeader, supplementGroups)
        : sectionsWithHeader;
    const hasMediaSection = sectionsWithNotices.some(isOrganMapSection);
    const mediaSections =
      reportPhotos.length > 0 && !hasMediaSection
        ? insertReportMediaSection(sectionsWithNotices)
        : sectionsWithNotices;
    const baseSections =
      reportAddendums.length > 0 &&
      !mediaSections.some((section) => section.type === "addendum")
        ? [
            ...mediaSections,
            {
              type: "addendum",
              label: "Addendum",
              config: {},
            } as TemplateSection,
          ]
        : mediaSections;
    if (!paginate) return baseSections;

    const unrolled: TemplateSection[] = [];
    const addendumFragments = buildAddendumReportFragments(reportAddendums);
    const supplementNoticeFragments =
      buildReportSupplementNoticeFragments(supplementGroups);
    const firstMediaSectionIndex = baseSections.findIndex(isOrganMapSection);
    const organMapCount = new Set(
      sectionLayoutSpecimens
        .filter(hasApplicableOrganMap)
        .map((specimen) => specimen.organMapTemplateId)
    ).size;
    const mediaRowCount = documentViewData.legacyBaseline
      ? reportPhotos.length
      : Math.ceil((organMapCount + reportPhotos.length) / 3);
    baseSections.forEach((s, sectionIndex) => {
      if (s.type === "diagnosis") {
        if (isReadOnly) {
          unrolled.push(
            ...buildReportNarrativeSections({
              section: s,
              sectionIndex,
              units: sectionLayoutSpecimens.map((_specimen, specimenIndex) => ({
                contentHtml:
                  descriptionSectionLayout.diagnosisContents[specimenIndex] ||
                  "",
                key: String(specimenIndex),
                config: { perSpecimen: true, specimenIndex },
                showLabel: true,
              })),
              fragmentedNarrativeKeys,
              includeEmptyUnits: true,
              narrativeFragmentsByContent,
            })
          );
        } else if (sectionLayoutSpecimens.length === 0) {
          unrolled.push(s);
        } else {
          sectionLayoutSpecimens.forEach((_specimen, idx) => {
            unrolled.push({
              ...s,
              config: {
                ...s.config,
                specimenIndex: idx,
                showHeading: idx === 0,
              },
            });
          });
        }
      } else if (isReadOnly && s.type === "comments") {
        unrolled.push(
          ...buildReportNarrativeSections({
            section: s,
            sectionIndex,
            units: commentSectionLayout.map((comment, commentIndex) => ({
              contentHtml: comment.commentText,
              key: comment.id,
              config: { commentIndex },
              showLabel: false,
            })),
            fragmentedNarrativeKeys,
            narrativeFragmentsByContent,
          })
        );
      } else if (isReadOnly && isSpecimenDescriptionSection(s)) {
        const specimenContents =
          s.type === "microscopic_description"
            ? descriptionSectionLayout.microscopicContents
            : descriptionSectionLayout.grossContents;
        const combinedContent =
          s.type === "microscopic_description"
            ? descriptionSectionLayout.combinedMicroscopicContent
            : descriptionSectionLayout.combinedGrossContent;
        unrolled.push(
          ...buildSpecimenDescriptionReportSections({
            section: s,
            sectionIndex,
            specimenContents,
            combinedContent,
            fragmentedDescriptionKeys: fragmentedNarrativeKeys,
            narrativeFragmentsByContent,
          })
        );
      } else if (sectionIndex === firstMediaSectionIndex && mediaRowCount > 1) {
        Array.from({ length: mediaRowCount }, (_, mediaRowIndex) =>
          unrolled.push({
            ...s,
            config: { ...s.config, mediaRowIndex },
          })
        );
      } else if (s.type === "report_supplement_notices") {
        const noticeFragments = s.config?.reportSupplementFragment
          ? [
              s.config.reportSupplementFragment,
              ...supplementNoticeFragments.filter(
                (fragment) => fragment.groupType !== "addendum"
              ),
            ]
          : supplementNoticeFragments;
        selectSupplementNoticeFragments(s, noticeFragments).forEach(
          (fragment) => {
            unrolled.push({
              ...s,
              config: {
                ...s.config,
                reportSupplementFragment: fragment,
              },
            });
          }
        );
      } else if (s.type === "addendum" && reportAddendums.length > 0) {
        addendumFragments.forEach((fragment) => {
          unrolled.push({
            ...s,
            config: {
              ...s.config,
              addendumIndex: fragment.addendumIndex,
              addendumFragmentIndex: fragment.fragmentIndex,
              addendumFragmentContentHtml: fragment.contentHtml,
              addendumShowHeading: fragment.showHeading,
              addendumShowSignature: fragment.showSignature,
              addendumPhotoRowIndex: fragment.photoRowIndex,
            },
          });
        });
      } else {
        unrolled.push(s);
      }
    });
    return unrolled;
  }, [
    descriptionSectionLayout,
    commentSectionLayout,
    documentViewData,
    fragmentedNarrativeKeys,
    isReadOnly,
    includeLegacyBaseline,
    narrativeFragmentsByContent,
    paginate,
    reportPhotos.length,
    sectionLayoutSpecimens,
  ]);
  const firstOrganMapSectionIndex = useMemo(
    () => sections.findIndex(isOrganMapSection),
    [sections]
  );
  const { headerSectionIndex, headerSection } = useMemo(() => {
    const index = sections.findIndex((section) => section.type === "header");
    return {
      headerSectionIndex: index,
      headerSection: index >= 0 ? sections[index] : undefined,
    };
  }, [sections]);
  const useContinuationHeaderOnFirstPage = useMemo(
    () =>
      documentViewData?.template.templateContent.metadata
        ?.suppressSupplementNotices === true &&
      sections.some((section) => section.type === "addendum") &&
      sections.every((section) =>
        STANDALONE_ADDENDUM_SECTION_TYPES.has(section.type)
      ),
    [documentViewData, sections]
  );
  const firstPageHeaderPageIndex = useMemo(
    () => (useContinuationHeaderOnFirstPage ? 1 : 0),
    [useContinuationHeaderOnFirstPage]
  );
  const forcedPageBreakBeforeIndexes = useMemo(
    () =>
      new Set(
        sections.flatMap((section, sectionIndex) =>
          section.type === "addendum" &&
          section.config?.addendumShowHeading !== false
            ? [sectionIndex]
            : []
        )
      ),
    [sections]
  );
  const getReportPhotosForSection = useCallback(
    (sectionIndex: number) => {
      const section = sections[sectionIndex];
      if (section?.type === "addendum") return addendumReportPhotos;
      return sectionIndex === firstOrganMapSectionIndex ||
        sections[sectionIndex]?.config?.mediaRowIndex !== undefined
        ? reportPhotos
        : undefined;
    },
    [firstOrganMapSectionIndex, reportPhotos, addendumReportPhotos, sections]
  );

  // Refs + state for paginated mode — hooks must be unconditional
  const measureRefs = useRef<(HTMLDivElement | null)[]>([]);
  const measureContainerRef = useRef<HTMLDivElement | null>(null);
  const continuationHeaderMeasureRef = useRef<HTMLDivElement | null>(null);
  const renderedPageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const paginationGeneration = useRef(0);
  const paginationSettledFired = useRef(false);
  const paginationFailedFired = useRef(false);
  const overflowRepairPasses = useRef(0);
  const [sectionHeights, setSectionHeights] = useState<number[]>([]);
  const [sectionHeightsGeneration, setSectionHeightsGeneration] = useState<
    number | null
  >(null);
  const [continuationHeaderHeight, setContinuationHeaderHeight] = useState<
    number | null
  >(null);
  const [renderedOverflowReserve, setRenderedOverflowReserve] = useState(0);
  const [paginationValidated, setPaginationValidated] = useState(false);
  const [fontsReady, setFontsReady] = useState(false);
  const [imagesReady, setImagesReady] = useState(false);
  const [imagesReadyGeneration, setImagesReadyGeneration] = useState<
    number | null
  >(null);
  const [measuredLayoutRevision, setMeasuredLayoutRevision] = useState(0);
  const handleMeasuredLayoutChange = useCallback(() => {
    paginationGeneration.current += 1;
    paginationSettledFired.current = false;
    setPaginationValidated(false);
    setMeasuredLayoutRevision((revision) => revision + 1);
    onPaginationPending?.();
  }, [onPaginationPending]);

  useLayoutEffect(() => {
    const container = measureContainerRef.current;
    if (!paginate || !container) return;
    return observeReportLayout(container, handleMeasuredLayoutChange);
  }, [paginate, sections, handleMeasuredLayoutChange]);

  const measuredReportPhotos = useMemo(
    () => [
      ...reportPhotos,
      ...addendumReportPhotos.filter((photo) =>
        documentViewData?.addendums.some((entry) =>
          entry.reportPhotoIds?.includes(photo.image.id)
        )
      ),
    ],
    [reportPhotos, addendumReportPhotos, documentViewData]
  );
  const reportPhotoStateKey = measuredReportPhotos
    .map(
      (photo) =>
        `${photo.image.id}:${photo.url || "pending"}:${
          photo.failed ? "failed" : "ready"
        }`
    )
    .join("|");
  const hasPendingReportPhotos = measuredReportPhotos.some(
    (photo) => !photo.url && !photo.failed
  );

  // When fonts finish loading, trigger a re-measurement. Without this, the
  // initial measurement runs with system fallback fonts whose metrics differ
  // from Times New Roman, causing the disclaimer and tail sections to
  // miscalculate and land on a spurious extra page in the delivered PDF.
  useEffect(() => {
    document.fonts.ready.then(() => setFontsReady(true));
  }, []);

  // After fonts load, wait for every <img> in the measurement container to
  // decode before allowing the layout-measurement pass to run. Images such as
  // organ-map diagrams have offsetHeight === 0 until they are loaded, so
  // measuring before decode causes the section to appear far shorter than it
  // actually is — leading to cut-off content or incorrect page placement.
  useEffect(() => {
    if (!paginate || !documentViewData || !fontsReady) return;
    const generation = paginationGeneration.current + 1;
    paginationGeneration.current = generation;
    paginationSettledFired.current = false;
    paginationFailedFired.current = false;
    overflowRepairPasses.current = 0;
    setPaginationValidated(false);
    setSectionHeightsGeneration(null);
    setContinuationHeaderHeight(null);
    setRenderedOverflowReserve(0);
    onPaginationPending?.();
    setImagesReady(false);
    setImagesReadyGeneration(null);
    if (hasPendingReportPhotos || document.fonts.status === "loading") return;
    const container = measureContainerRef.current;
    if (!container) {
      setImagesReady(true);
      setImagesReadyGeneration(generation);
      return;
    }
    if (container.querySelector('[data-report-layout-failed="true"]')) {
      paginationFailedFired.current = true;
      onPaginationFailed?.();
      return;
    }
    if (container.querySelector('[data-report-layout-pending="true"]')) return;
    const imgs = Array.from(
      container.querySelectorAll<HTMLImageElement>("img")
    );
    if (imgs.length === 0) {
      setImagesReady(true);
      setImagesReadyGeneration(generation);
      return;
    }
    let cancelled = false;
    Promise.allSettled(
      imgs.map((img) =>
        img.complete && img.naturalWidth > 0 ? Promise.resolve() : img.decode()
      )
    ).then((results) => {
      if (!cancelled && generation === paginationGeneration.current) {
        if (results.some((result) => result.status === "rejected")) {
          paginationFailedFired.current = true;
          onPaginationFailed?.();
          return;
        }
        setImagesReady(true);
        setImagesReadyGeneration(generation);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [
    paginate,
    documentViewData,
    fontsReady,
    hasPendingReportPhotos,
    onPaginationPending,
    onPaginationFailed,
    paginationContentKey,
    reportPhotoStateKey,
    measuredLayoutRevision,
    sections,
  ]);

  // Measure hidden section divs once documentViewData is available AND fonts
  // are loaded. Including fontsReady in the deps ensures a second measurement
  // pass fires after Times New Roman is active, so all heights reflect the
  // actual rendered typography.
  useLayoutEffect(() => {
    if (
      !paginate ||
      !documentViewData ||
      !imagesReady ||
      imagesReadyGeneration !== paginationGeneration.current
    )
      return;
    const heights = sections.map(
      (_, i) => measureRefs.current[i]?.offsetHeight ?? 0
    );
    if (heights.every((h) => h === 0)) return;
    setSectionHeights((prev) => {
      if (
        prev.length === heights.length &&
        prev.every((h, i) => h === heights[i])
      )
        return prev;
      return heights;
    });
    setContinuationHeaderHeight(
      continuationHeaderMeasureRef.current?.offsetHeight ?? 0
    );
    setSectionHeightsGeneration(imagesReadyGeneration);
    // fontsReady + imagesReady: measurement re-runs only after Times New Roman
    // and all organ-map/logo images in the measurement container have decoded.
  }, [
    paginate,
    documentViewData,
    fontsReady,
    imagesReady,
    imagesReadyGeneration,
    paginationContentKey,
    sections,
  ]);

  const narrativeKeysNeedingFragmentation = useMemo(() => {
    if (
      !paginate ||
      sectionHeights.length === 0 ||
      continuationHeaderHeight === null ||
      sectionHeightsGeneration !== paginationGeneration.current
    ) {
      return [];
    }
    const headerHeight =
      headerSectionIndex >= 0 ? sectionHeights[headerSectionIndex] ?? 0 : 0;
    const narrativeKey = findNextReportNarrativeKeyToFragment({
      sectionHeights,
      headerIndex: headerSectionIndex,
      availableHeight: pageContentHeight - headerHeight,
      continuationAvailableHeight: pageContentHeight - continuationHeaderHeight,
      renderedOverflowReserve,
      forcedPageBreakBeforeIndexes,
      narrativeKeys: sections.map(
        (section) => section.config?.reportNarrativeKey
      ),
      narrativeCanFragment: sections.map(
        (section) => section.config?.reportNarrativeCanFragment === true
      ),
      narrativeFragmentIndexes: sections.map(
        (section) => section.config?.reportNarrativeFragmentIndex
      ),
    });
    return narrativeKey ? [narrativeKey] : [];
  }, [
    paginate,
    pageContentHeight,
    continuationHeaderHeight,
    forcedPageBreakBeforeIndexes,
    headerSectionIndex,
    renderedOverflowReserve,
    sectionHeights,
    sectionHeightsGeneration,
    sections,
  ]);

  useLayoutEffect(() => {
    if (narrativeKeysNeedingFragmentation.length === 0) return;
    setNarrativeFragmentation((current) => {
      const fragmentedKeys = new Set(
        current.contentKey === paginationContentKey
          ? current.fragmentedKeys
          : EMPTY_NARRATIVE_FRAGMENT_KEYS
      );
      narrativeKeysNeedingFragmentation.forEach((key) =>
        fragmentedKeys.add(key)
      );
      return { contentKey: paginationContentKey, fragmentedKeys };
    });
  }, [narrativeKeysNeedingFragmentation, paginationContentKey]);

  const paginationLayout = useMemo(() => {
    if (
      !paginate ||
      sectionHeights.length === 0 ||
      !documentViewData ||
      continuationHeaderHeight === null ||
      narrativeKeysNeedingFragmentation.length > 0 ||
      sectionHeightsGeneration !== paginationGeneration.current ||
      sectionHeights.length !== sections.length
    )
      return null;
    const headerHeight =
      headerSectionIndex >= 0 ? sectionHeights[headerSectionIndex] ?? 0 : 0;
    return createReportPaginationLayout({
      sectionHeights,
      headerIndex: headerSectionIndex,
      availableHeight: pageContentHeight - headerHeight,
      continuationAvailableHeight: pageContentHeight - continuationHeaderHeight,
      renderedOverflowReserve,
      forcedPageBreakBeforeIndexes,
    });
  }, [
    paginate,
    pageContentHeight,
    sectionHeights,
    documentViewData,
    continuationHeaderHeight,
    headerSectionIndex,
    sectionHeightsGeneration,
    renderedOverflowReserve,
    forcedPageBreakBeforeIndexes,
    narrativeKeysNeedingFragmentation,
    sections.length,
  ]);
  const pages = useMemo(() => {
    if (!paginationLayout) return null;
    const containsOnlyCurrentSections = paginationLayout.pages.every((page) =>
      page.every(
        (sectionIndex) => sectionIndex >= 0 && sectionIndex < sections.length
      )
    );
    return containsOnlyCurrentSections ? paginationLayout.pages : null;
  }, [paginationLayout, sections.length]);
  const totalPageCount = useMemo(
    () => pageNumberOffset + (pages?.length ?? 0) + additionalPageCount,
    [additionalPageCount, pageNumberOffset, pages]
  );
  const sectionScales = paginationLayout?.sectionScales ?? {};
  const unreadableSectionIndexes =
    paginationLayout?.unreadableSectionIndexes ?? [];
  const hasUnreadableSections = unreadableSectionIndexes.length > 0;

  useLayoutEffect(() => {
    if (
      pages === null ||
      !fontsReady ||
      !imagesReady ||
      imagesReadyGeneration !== paginationGeneration.current ||
      sectionHeightsGeneration !== paginationGeneration.current
    ) {
      return;
    }
    if (hasUnreadableSections) {
      setPaginationValidated(false);
      if (!paginationFailedFired.current) {
        paginationFailedFired.current = true;
        onPaginationFailed?.();
      }
      return;
    }
    const overflow = Math.max(
      0,
      ...renderedPageRefs.current
        .slice(0, pages.length)
        .filter((page): page is HTMLDivElement => page !== null)
        .map(calculateRenderedPageOverflow)
    );
    if (overflow > 0) {
      setPaginationValidated(false);
      if (overflowRepairPasses.current >= MAX_OVERFLOW_REPAIR_PASSES) {
        if (!paginationFailedFired.current) {
          paginationFailedFired.current = true;
          onPaginationFailed?.();
        }
        return;
      }
      overflowRepairPasses.current += 1;
      setRenderedOverflowReserve(
        (currentReserve) =>
          currentReserve + overflow + RENDERED_OVERFLOW_CUSHION
      );
      return;
    }
    setPaginationValidated(true);
  }, [
    pages,
    fontsReady,
    imagesReady,
    imagesReadyGeneration,
    sectionHeightsGeneration,
    hasUnreadableSections,
    onPaginationFailed,
  ]);

  // Fire onPaginationSettled once pagination is complete with correct font metrics.
  // A ref prevents re-firing if pages or fontsReady update again.
  useEffect(() => {
    if (
      pages !== null &&
      fontsReady &&
      imagesReady &&
      paginationValidated &&
      imagesReadyGeneration === paginationGeneration.current &&
      sectionHeightsGeneration === paginationGeneration.current &&
      !paginationSettledFired.current
    ) {
      paginationSettledFired.current = true;
      onPaginationSettled?.(pages.length);
    }
  }, [
    pages,
    fontsReady,
    imagesReady,
    paginationValidated,
    imagesReadyGeneration,
    sectionHeightsGeneration,
    onPaginationSettled,
  ]);

  if (isLoading) {
    return (
      <Box
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 300,
        }}
      >
        <Loader size="md" />
      </Box>
    );
  }

  if (historicalAssetsUnavailable) {
    return <LegacyHistoricalReportUnavailable onError={onPaginationFailed} />;
  }

  if (error || !documentViewData) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red" m="md">
        Failed to load document view. Please try again.
      </Alert>
    );
  }

  // Shared props passed to every DocumentSection instance

  const sharedSectionProps = {
    documentViewData,
    caseData,
    specimens,
    diagnosisDataMap: diagnosisDataMap as any,
    grossingDataMap: grossingDataMap as any,
    clinicalHistoryNotes,
    indications,
    impressions,
    pathologistComment,
    pathologistComments,
    amendmentCommentContext,
    frozenInterpretation,
    isReadOnly,
    onClinicalHistoryChange,
    onIndicationsChange,
    onImpressionsChange,
    onCommentChange,
    onDiagnosisChange,
    onGrossChange,
    onAddAddendum,
    onAddAmendment,
    onAddCorrection,
    onCodesChange,
    liveCptDisplay,
    liveIcdDisplay,
    onHeaderSaved,
    macros,
    onMacroApplied,
    grossCodes,
    onGrossCodeApplied,
    onGrossVariableChange,
    subsiteOptionsBySource,
    diagnosisCategories,
    reviewSimulation,
  };

  const pageFooter = null;

  if (paginate) {
    return (
      <>
        <style>{DOCUMENT_STYLES}</style>

        {/* Off-screen measurement pass: render each section to get its height.
            Must mirror the rendered .pdf-page typography (font, size, line-height)
            below — otherwise sections measure at the modal's ambient font
            (~14-16px) but render at 12px Times, causing the pagination logic
            to reserve the wrong amount of space per section. */}
        <div
          ref={measureContainerRef}
          data-report-measure-container="true"
          style={{
            position: "fixed",
            left: -9999,
            top: 0,
            width: LETTER_PAGE_WIDTH - LETTER_MARGIN_H * 2,
            visibility: "hidden",
            pointerEvents: "none",
            ...REPORT_PAGE_FLOW_STYLE,
          }}
        >
          {sections.map((section, i) => (
            <div
              key={`measure-${section.type}-${i}`}
              ref={(el) => {
                measureRefs.current[i] = el;
              }}
            >
              <DocumentSection
                section={section}
                {...sharedSectionProps}
                reportPhotos={getReportPhotosForSection(i)}
                pageIndex={
                  section.type === "header"
                    ? firstPageHeaderPageIndex
                    : undefined
                }
              />
            </div>
          ))}
          {headerSection && (
            <div ref={continuationHeaderMeasureRef}>
              <DocumentSection
                section={headerSection}
                {...sharedSectionProps}
                pageIndex={1}
              />
            </div>
          )}
        </div>

        {/* While heights are not yet known, show a spinner */}
        {!pages ? (
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: 300,
            }}
          >
            <Loader size="md" />
          </Box>
        ) : hasUnreadableSections ? (
          <Alert
            data-report-pagination-error="true"
            icon={<IconAlertCircle size={16} />}
            color="red"
            m="md"
          >
            Report content could not be paginated without becoming difficult to
            read. Please divide the oversized content into smaller sections
            before generating the report.
          </Alert>
        ) : (
          pages.map((sectionIndices, pageIndex) => (
            <div
              key={pageIndex}
              ref={(element) => {
                renderedPageRefs.current[pageIndex] = element;
              }}
              className="pdf-page"
              style={{
                width: LETTER_PAGE_WIDTH,
                height: LETTER_PAGE_HEIGHT,
                overflow: "hidden",
                padding: `${LETTER_MARGIN_TOP}px ${LETTER_MARGIN_H}px ${LETTER_MARGIN_BOTTOM}px ${LETTER_MARGIN_H}px`,
                backgroundColor: "white",
                boxShadow:
                  "0 1px 3px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.1)",
                marginBottom: 16,
                marginLeft: "auto",
                marginRight: "auto",
                boxSizing: "border-box",
                ...REPORT_PAGE_FLOW_STYLE,
              }}
            >
              {headerSection && (
                <DocumentSection
                  key={`page-${pageIndex}-header`}
                  section={headerSection}
                  {...sharedSectionProps}
                  pageIndex={useContinuationHeaderOnFirstPage ? 1 : pageIndex}
                />
              )}
              {sectionIndices.map((sectionIndex) => (
                <div
                  key={`page-${pageIndex}-section-${sectionIndex}`}
                  data-report-section-scale={
                    sectionScales[sectionIndex] ?? undefined
                  }
                  style={
                    sectionScales[sectionIndex]
                      ? ({
                          zoom: sectionScales[sectionIndex],
                        } as React.CSSProperties)
                      : undefined
                  }
                >
                  <DocumentSection
                    section={sections[sectionIndex]}
                    {...sharedSectionProps}
                    reportPhotos={getReportPhotosForSection(sectionIndex)}
                  />
                </div>
              ))}
              {pageIndex === pages.length - 1 && pageFooter}
              <div style={{ marginTop: "auto" }}>
                {documentViewData.legacyBaseline ? (
                  <LegacyHistoricalFooter
                    patientName={footerPatientName}
                    caseNumber={footerCaseNumber}
                    pageNumber={pageNumberOffset + pageIndex + 1}
                    totalPages={totalPageCount}
                  />
                ) : (
                  <DocumentFooter
                    patientName={footerPatientName}
                    caseNumber={footerCaseNumber}
                    pageNumber={pageNumberOffset + pageIndex + 1}
                    totalPages={totalPageCount}
                  />
                )}
              </div>
            </div>
          ))
        )}
      </>
    );
  }

  const documentContent = (
    <>
      <style>{DOCUMENT_STYLES}</style>
      <Box
        className="document-view-container"
        style={{
          maxWidth: 800,
          margin: "0 auto",
          padding: "24px 40px",
          fontFamily: "'Times New Roman', Times, serif",
          fontSize: 12,
          lineHeight: 1.5,
          backgroundColor: "white",
          minHeight: "100%",
        }}
      >
        {sections.map((section, index) => (
          <DocumentSection
            key={`${section.type}-${index}`}
            section={section}
            {...sharedSectionProps}
            reportPhotos={getReportPhotosForSection(index)}
            pageIndex={
              section.type === "header" ? firstPageHeaderPageIndex : undefined
            }
          />
        ))}
        {pageFooter}
      </Box>
    </>
  );

  if (noScrollWrapper) return documentContent;

  return (
    <ScrollArea style={{ flex: 1 }} styles={{ viewport: { padding: "12px" } }}>
      {documentContent}
    </ScrollArea>
  );
}
